import { fetchAuth } from '../../../shared/api/ApiService'
import {
    ICreateLegalDocument,
    ICreateLegalDocumentVersion,
    ILegalDocument,
    ILegalDocumentVersion,
    IPatchLegalDocument,
    IPatchLegalDocumentVersion,
    TLegalDocumentStatus,
    TLegalDocumentType,
    TLegalDocumentVersionStatus,
} from '../model/types'

type TRecord = Record<string, unknown>

const apiUrl = (path: string) => `${process.env.REACT_APP_SERVER_URL_ADMIN}${path}`

const stringValue = (source: TRecord, camelCase: string, snakeCase = camelCase): string => {
    const value = source[camelCase] ?? source[snakeCase]
    return value === undefined || value === null ? '' : String(value)
}

const normalizeDocumentStatus = (value: unknown): TLegalDocumentStatus => {
    const status = String(value ?? '')
    const values: Record<string, TLegalDocumentStatus> = {
        active: 'ADMIN_DOCUMENT_STATUS_ACTIVE',
        hidden: 'ADMIN_DOCUMENT_STATUS_HIDDEN',
        archived: 'ADMIN_DOCUMENT_STATUS_ARCHIVED',
        '1': 'ADMIN_DOCUMENT_STATUS_ACTIVE',
        '2': 'ADMIN_DOCUMENT_STATUS_HIDDEN',
        '3': 'ADMIN_DOCUMENT_STATUS_ARCHIVED',
    }

    if (status in values) return values[status]
    if (status.startsWith('ADMIN_DOCUMENT_STATUS_') && status !== 'ADMIN_DOCUMENT_STATUS_UNSPECIFIED') {
        return status as TLegalDocumentStatus
    }
    return 'ADMIN_DOCUMENT_STATUS_ACTIVE'
}

const normalizeVersionStatus = (value: unknown): TLegalDocumentVersionStatus => {
    const status = String(value ?? '')
    const values: Record<string, TLegalDocumentVersionStatus> = {
        draft: 'ADMIN_DOCUMENT_VERSION_STATUS_DRAFT',
        published: 'ADMIN_DOCUMENT_VERSION_STATUS_PUBLISHED',
        archived: 'ADMIN_DOCUMENT_VERSION_STATUS_ARCHIVED',
        '1': 'ADMIN_DOCUMENT_VERSION_STATUS_DRAFT',
        '2': 'ADMIN_DOCUMENT_VERSION_STATUS_PUBLISHED',
        '3': 'ADMIN_DOCUMENT_VERSION_STATUS_ARCHIVED',
    }

    if (status in values) return values[status]
    if (status.startsWith('ADMIN_DOCUMENT_VERSION_STATUS_') && status !== 'ADMIN_DOCUMENT_VERSION_STATUS_UNSPECIFIED') {
        return status as TLegalDocumentVersionStatus
    }
    return 'ADMIN_DOCUMENT_VERSION_STATUS_DRAFT'
}

const normalizeDocumentType = (value: unknown): TLegalDocumentType => {
    const type = String(value ?? '')
    const values: Record<string, TLegalDocumentType> = {
        offer: 'ADMIN_DOCUMENT_TYPE_OFFER',
        personal_data_processing_policy: 'ADMIN_DOCUMENT_TYPE_PERSONAL_DATA_PROCESSING_POLICY',
        personal_data_processing_consent: 'ADMIN_DOCUMENT_TYPE_PERSONAL_DATA_PROCESSING_CONSENT',
        '1': 'ADMIN_DOCUMENT_TYPE_OFFER',
        '2': 'ADMIN_DOCUMENT_TYPE_PERSONAL_DATA_PROCESSING_POLICY',
        '3': 'ADMIN_DOCUMENT_TYPE_PERSONAL_DATA_PROCESSING_CONSENT',
    }

    if (type in values) return values[type]
    if (type.startsWith('ADMIN_DOCUMENT_TYPE_') && type !== 'ADMIN_DOCUMENT_TYPE_UNSPECIFIED') {
        return type as TLegalDocumentType
    }
    return 'ADMIN_DOCUMENT_TYPE_OFFER'
}

const mapDocument = (source: TRecord): ILegalDocument => ({
    id: stringValue(source, 'id'),
    adminId: stringValue(source, 'adminId', 'admin_id'),
    type: normalizeDocumentType(source.type),
    code: stringValue(source, 'code'),
    title: stringValue(source, 'title'),
    description: stringValue(source, 'description'),
    status: normalizeDocumentStatus(source.status),
    currentPublishedVersionId: stringValue(source, 'currentPublishedVersionId', 'current_published_version_id') || '0',
    createdBy: stringValue(source, 'createdBy', 'created_by'),
    updatedBy: stringValue(source, 'updatedBy', 'updated_by'),
    createdAt: stringValue(source, 'createdAt', 'created_at'),
    updatedAt: stringValue(source, 'updatedAt', 'updated_at'),
})

const mapVersion = (source: TRecord): ILegalDocumentVersion => ({
    id: stringValue(source, 'id'),
    documentId: stringValue(source, 'documentId', 'document_id'),
    adminId: stringValue(source, 'adminId', 'admin_id'),
    number: stringValue(source, 'number'),
    fileName: stringValue(source, 'fileName', 'file_name'),
    mimeType: stringValue(source, 'mimeType', 'mime_type'),
    sizeBytes: stringValue(source, 'sizeBytes', 'size_bytes'),
    checksum: stringValue(source, 'checksum'),
    status: normalizeVersionStatus(source.status),
    publishedAt: stringValue(source, 'publishedAt', 'published_at'),
    changeNote: stringValue(source, 'changeNote', 'change_note'),
    createdBy: stringValue(source, 'createdBy', 'created_by'),
    updatedBy: stringValue(source, 'updatedBy', 'updated_by'),
    createdAt: stringValue(source, 'createdAt', 'created_at'),
    updatedAt: stringValue(source, 'updatedAt', 'updated_at'),
})

const payload = async (response: Response): Promise<TRecord> => response.json() as Promise<TRecord>

class LegalDocumentsService {
    async listDocuments(): Promise<ILegalDocument[]> {
        const response = await payload(await fetchAuth(apiUrl('/api/v1/legal-documents')))
        const documents = response.documents
        return Array.isArray(documents) ? documents.map(item => mapDocument(item as TRecord)) : []
    }

    async getDocument(documentId: string): Promise<ILegalDocument> {
        const response = await payload(await fetchAuth(apiUrl(`/api/v1/legal-documents/${documentId}`)))
        return mapDocument((response.document ?? {}) as TRecord)
    }

    async createDocument(data: ICreateLegalDocument): Promise<ILegalDocument> {
        const response = await payload(await fetchAuth(apiUrl('/api/v1/legal-documents'), {
            method: 'POST',
            body: JSON.stringify(data),
        }))
        return mapDocument((response.document ?? {}) as TRecord)
    }

    async patchDocument(documentId: string, data: IPatchLegalDocument): Promise<ILegalDocument> {
        const response = await payload(await fetchAuth(apiUrl(`/api/v1/legal-documents/${documentId}`), {
            method: 'PATCH',
            body: JSON.stringify(data),
        }))
        return mapDocument((response.document ?? {}) as TRecord)
    }

    async listDocumentVersions(documentId: string): Promise<ILegalDocumentVersion[]> {
        const response = await payload(await fetchAuth(apiUrl(`/api/v1/legal-documents/${documentId}/versions`)))
        const versions = response.versions
        return Array.isArray(versions) ? versions.map(item => mapVersion(item as TRecord)) : []
    }

    async createDocumentVersion(documentId: string, data: ICreateLegalDocumentVersion): Promise<ILegalDocumentVersion> {
        const response = await payload(await fetchAuth(apiUrl(`/api/v1/legal-documents/${documentId}/versions`), {
            method: 'POST',
            body: JSON.stringify(data),
        }))
        return mapVersion((response.version ?? {}) as TRecord)
    }

    async patchDocumentVersion(versionId: string, data: IPatchLegalDocumentVersion): Promise<ILegalDocumentVersion> {
        const response = await payload(await fetchAuth(apiUrl(`/api/v1/legal-documents/versions/${versionId}`), {
            method: 'PATCH',
            body: JSON.stringify(data),
        }))
        return mapVersion((response.version ?? {}) as TRecord)
    }

    async deleteDocumentVersion(versionId: string): Promise<void> {
        await fetchAuth(apiUrl(`/api/v1/legal-documents/versions/${versionId}`), {
            method: 'DELETE',
        })
    }

    async makeDocumentVersionPrimary(versionId: string): Promise<ILegalDocumentVersion> {
        const response = await payload(await fetchAuth(apiUrl(`/api/v1/legal-documents/versions/${versionId}/make-primary`), {
            method: 'POST',
        }))
        return mapVersion((response.version ?? {}) as TRecord)
    }

    async downloadDocumentVersion(versionId: string): Promise<Blob> {
        const response = await fetchAuth(apiUrl(`/api/v1/legal-documents/versions/${versionId}/download`))
        return response.blob()
    }
}

export const legalDocumentsService = new LegalDocumentsService()

export const readFileAsBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Не удалось прочитать файл'))
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '')
    reader.readAsDataURL(file)
})

export const downloadLegalDocumentVersion = (version: ILegalDocumentVersion, file: Blob): void => {
    const url = URL.createObjectURL(file)
    const link = document.createElement('a')
    link.href = url
    link.download = version.fileName || 'legal-document'
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
}
